import { type DealDocumentsResult, groupQuotations, isAccountsOnly, isQuotationDocument, type PopupCard, type PopupDealRef, type PopupEvent, type PopupsResult } from '@crm/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { api, errorDetail } from '../lib/api';
import { formatDate, formatMoney, riyadhDay } from '../lib/format';
import { useSession } from '../lib/session';
import { closeAnimated } from '../lib/motion';
import { confirmDialog } from '../lib/dialogs';
import { Icon } from './Icon';

const errMsg = errorDetail;
const dealUrl = (id: string) => `/o/opportunity/${id}`;
const SOURCE: Record<string, string> = { tender: 'Tender', on_hand: 'On-Hand', not_set: 'Not set' };

/** One popup at a time: events first (oldest first), then the daily cards. Shown on every page. */
/** Quotation ready: the deal's quotation files, each with Download (sales sends it without opening the deal). */
export function PopupQuotations({ opportunityId }: { opportunityId: string }) {
  const q = useQuery({ queryKey: ['deal-docs', opportunityId], queryFn: () => api<DealDocumentsResult>(`/api/opportunities/${opportunityId}/documents`), retry: false });
  // The latest revision of each option (older ones and copies are on the deal page).
  const quotes = groupQuotations((q.data?.documents ?? []).filter(isQuotationDocument)).map((g) => g.latest);
  if (!quotes.length) return null;
  return (
    <ul className="list">
      {quotes.slice(0, 5).map((d) => (
        <li key={d.id}><span><Icon name="file" tone="pdf" /><b>{d.msName ?? d.fileName}</b></span>
          {d.msWebUrl?.startsWith('/api/documents/') && <a className="btn small primary" href={d.msWebUrl} title="Download to send to the customer"><Icon name="download" />Download</a>}</li>
      ))}
    </ul>
  );
}

export function Popups() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['popups'],
    queryFn: () => api<PopupsResult>('/api/popups'),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  // Put away in this page view (the server also remembers, so a failed save doesn't keep it on screen).
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const hide = (k: string) => setHidden((h) => new Set(h).add(k));
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['popups'] });
    void qc.invalidateQueries({ queryKey: ['notifications'] });
  };
  const seen = useMutation({ mutationFn: (id: string) => api('/api/popups/seen', { method: 'POST', body: { id } }), onSettled: refresh });
  const dismiss = useMutation({ mutationFn: (key: string) => api('/api/popups/dismiss', { method: 'POST', body: { key } }), onSettled: refresh });

  const event = q.data?.events.find((e) => !hidden.has(e.id));
  const card = event ? undefined : q.data?.cards.find((c) => !hidden.has(c.key));
  const closeNow = () => {
    if (event) { hide(event.id); seen.mutate(event.id); } else if (card) { hide(card.key); dismiss.mutate(card.key); }
  };
  // Closing: the pop-up shrinks away first, then the next one (if any) slides up.
  const pop = useRef<HTMLDivElement>(null);
  const close = () => closeAnimated(pop.current, closeNow);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !document.querySelector('dialog[open]') && (event || card) && event?.kind !== 'priority.high' && event?.kind !== 'project.priority_high') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });
  if (!event && !card) return null;
  const urgent = event?.kind === 'priority.high' || event?.kind === 'project.priority_high';
  // Several waiting: shown as a small pile behind this one; closing it slides the next one up.
  const waiting = (q.data?.events.filter((e) => !hidden.has(e.id)).length ?? 0) + (q.data?.cards.filter((c) => !hidden.has(c.key)).length ?? 0);
  const more = Math.max(0, waiting - 1);
  return (
    <div className="modal-backdrop popup-backdrop" role="presentation">
      <div ref={pop} key={event?.id ?? card?.key} className={`modal popup${urgent ? ' urgent' : ''}`} data-stack={Math.min(more, 2)} role="dialog" aria-modal="true" aria-labelledby="popup-title">
        {more > 0 && <span className="popup-more" title="Waiting after this one">+{more} more</span>}
        {event ? <EventPopup e={event} onDone={close} /> : card ? <CardPopup c={card} onDone={close} /> : null}
      </div>
    </div>
  );
}

function Shell({ tag, title, children, actions }: { tag: string; title: ReactNode; children?: ReactNode; actions: ReactNode }) {
  return (
    <>
      <p className="popup-tag">{tag}</p>
      <h2 id="popup-title">{title}</h2>
      {children}
      <div className="actions popup-actions">{actions}</div>
    </>
  );
}

/** Runs an action, then closes the popup; shows the error instead if it fails. */
function useAction(onDone: () => void) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: (fn: () => Promise<unknown>) => fn(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['record'] });
      void qc.invalidateQueries({ queryKey: ['records'] });
      void qc.invalidateQueries({ queryKey: ['presales-queue'] });
      void qc.invalidateQueries({ queryKey: ['deal-docs'] });
      onDone();
    },
    onError: (e) => setError(errMsg(e)),
  });
  return { run: (fn: () => Promise<unknown>) => { setError(null); m.mutate(fn); }, busy: m.isPending, error };
}

function EventPopup({ e, onDone }: { e: PopupEvent; onDone: () => void }) {
  const navigate = useNavigate();
  const { permissions } = useSession();
  const a = useAction(onDone);
  const [comment, setComment] = useState('');
  const id = e.recordId;
  const open = (hash = '') => {
    onDone();
    if (e.objectName === 'accounts_center') { navigate(isAccountsOnly(permissions) ? '/' : '/accounts'); return; }
    if (e.objectName === 'tool') { navigate(`/projects/tools${id ? `?tool=${id}` : ''}`); return; }
    if (e.objectName === 'shipment') { navigate(`/logistics${id ? `?show=all&shipment=${id}` : ''}`); return; }
    if (!id) return;
    navigate(e.objectName === 'delivery_project' ? `/projects/${id}` : e.objectName === 'delivery_po' ? `/projects/po/${id}`
      : e.objectName === 'delivery_priority' ? `/projects/priority/${id}` : e.objectName === 'delivery_vo' ? `/projects/vo/${id}` : `${dealUrl(id)}${hash}`);
  };
  const body = e.body ? <p className="popup-body">{e.body}</p> : null;
  const from = e.actorName ? <p className="muted small">From {e.actorName}</p> : null;
  const err = a.error && <p className="form-error" role="alert">{a.error}</p>;
  // Projects-only users can't open deals; the deal button is shown to people who can.
  // The button names what it opens (a tool reminder opens the tool, not a deal).
  const openLabel = ({
    opportunity: 'Open deal', delivery_project: 'Open project', delivery_po: 'Open purchase order', delivery_priority: 'Open request',
    delivery_vo: 'Open variation order', tool: 'Open tool', shipment: 'Open shipment', accounts_center: 'Open Accounts',
  } as Record<string, string>)[e.objectName ?? ''] ?? 'Open';
  const openBtn = (id || e.objectName === 'tool' || e.objectName === 'accounts_center') && (e.objectName !== 'opportunity' || permissions.objects.opportunity.read)
    && <button className="btn" onClick={() => open()}>{openLabel}</button>;
  const later = <button className="btn ghost" onClick={onDone}>Later</button>;
  // Pre-sales work (start, clarification, quotation ready, submittals) is done in My Desk.
  const workbenchBtn = <button className="btn primary" onClick={() => { onDone(); navigate('/presales'); }}>Open in My Desk</button>;

  switch (e.kind) {
    case 'presales.handoff':
      // Handed to the team: the pre-sales manager assigns it in My Desk (nothing to acknowledge yet).
      if (e.title.startsWith('New deal for pre-sales — assign it')) {
        return (
          <Shell tag="New deal for the pre-sales team" title={e.deal?.name ?? e.title} actions={<>{later}{openBtn}
            <button className="btn primary" onClick={() => { onDone(); navigate('/presales'); }}>Assign in My Desk</button></>}>
            <p>Choose the engineer and the internal deadline in <b>To assign</b>.</p>{body}{from}
          </Shell>
        );
      }
      return (
        <Shell tag="Pre-sales" title={e.deal?.name ?? e.title} actions={<>{later}{openBtn}{workbenchBtn}</>}>
          <p>{e.title}</p>{body}{from}
          {e.deal?.presalesStatus === 'ready' && <p className="small muted">Press <b>Start</b> on the deal’s row in My Desk.</p>}
        </Shell>
      );
    case 'presales.support':
      return (
        <Shell tag={/high priority/i.test(e.title) ? 'HIGH PRIORITY · support needed' : 'Support needed'} title={e.deal?.name ?? e.title} actions={<>{later}{openBtn}
          <button className="btn primary" disabled={a.busy} onClick={() => a.run(() => api(`/api/opportunities/${id}/presales/support/accept`, { method: 'POST', body: {} }))}>Accept support</button></>}>
          <p>You were added as a support engineer on this deal.</p>{body}{from}{err}
        </Shell>
      );
    case 'presales.support_late':
    case 'presales.support_accepted':
    case 'presales.quotation_sent':
      return (
        <Shell tag={e.kind === 'presales.support_late' ? 'Support not accepted' : e.kind === 'presales.quotation_sent' ? 'Quotation sent' : 'Support accepted'} title={e.title} actions={<>{later}{openBtn}</>}>
          {body}{from}
        </Shell>
      );
    case 'priority.high':
      return (
        <Shell tag="HIGH PRIORITY" title={e.deal?.name ?? e.title} actions={<>{openBtn}
          {permissions.system.presalesWork ? workbenchBtn : <button className="btn primary" onClick={onDone}>Got it</button>}</>}>
          <p><b>Please handle this deal as soon as possible.</b></p>{body}{from}
        </Shell>
      );
    case 'presales.clarification':
      return (
        <Shell tag="Pre-sales needs documents" title={e.deal?.name ?? e.title} actions={<>{later}
          <button className="btn primary" onClick={() => open('#docs-title')}>Upload documents</button></>}>
          {e.body ? <blockquote className="popup-quote">{e.body}</blockquote> : <p>Pre-sales is missing documents or answers for this deal.</p>}{from}
        </Shell>
      );
    case 'tool.calibration':
      return (
        <Shell tag="Tool calibration" title={e.title} actions={<>{later}{openBtn}</>}>
          {body}
        </Shell>
      );
    case 'logistics.handover':
    case 'logistics.handover_done':
      return (
        <Shell tag={e.kind === 'logistics.handover' ? 'Logistics handover' : 'Logistics handover answered'} title={e.title} actions={<>{later}{openBtn}</>}>
          {body}{from}
        </Shell>
      );
    case 'logistics.alert':
      return (
        <Shell tag="Delivery at risk" title={e.title} actions={<>{later}{openBtn}</>}>
          {body}
        </Shell>
      );
    case 'tool.handover':
      return (
        <Shell tag="Tool handed to you" title={e.title} actions={<>{later}{openBtn}</>}>
          {body}{from}
        </Shell>
      );
    case 'intel.assigned':
      return (
        <Shell tag="Action assigned to you" title={e.title} actions={<>{later}
          <button className="btn primary" onClick={() => open('#engine-title')}>Open the deal</button></>}>
          {body}{from}
        </Shell>
      );
    case 'quote.approval':
      return (
        <Shell tag="GP approval needed" title={e.title} actions={<>{later}
          <button className="btn primary" onClick={() => open('#engine-title')}>Review and decide</button></>}>
          {e.body ? <p>{e.body}</p> : null}
        </Shell>
      );
    case 'quote.decided':
      return (
        <Shell tag="GP decision" title={e.title} actions={<>{later}{openBtn}</>}>
          {e.body ? <blockquote className="popup-quote">{e.body}</blockquote> : null}{from}
        </Shell>
      );
    case 'presales.submittal_revise':
      return (
        <Shell tag="Submittal returned" title={e.title} actions={<>{later}
          {permissions.system.presalesWork ? workbenchBtn : <button className="btn primary" onClick={() => open('#submittals-title')}>Open submittals</button>}</>}>
          {e.body && <blockquote className="popup-quote">{e.body}</blockquote>}
          <p>{permissions.system.presalesWork ? 'Revise it and send the next revision from My Desk (Submittals on the deal’s row).' : 'Pre-sales revises it and sends the next revision.'}</p>{from}
        </Shell>
      );
    case 'presales.quotation_ready': {
      const canMove = !!e.deal && !e.deal.isClosed && !e.deal.inQuoteStage && !!e.deal.quoteStageId;
      return (
        <Shell tag="Quotation ready" title={e.deal?.name ?? e.title} actions={<>{later}{openBtn}
          {canMove && <button className="btn primary" disabled={a.busy}
            onClick={() => a.run(() => api(`/api/records/opportunity/${id}`, { method: 'PATCH', body: { stageId: e.deal!.quoteStageId } }))}>Sent it: move to Quotation Sent</button>}</>}>
          <p>Pre-sales has finished the quotation. Send it to the customer{canMove ? ', then move the deal to Quotation Sent.' : '.'}</p>
          {id && <PopupQuotations opportunityId={id} />}{from}{err}
        </Shell>
      );
    }
    case 'priority.requested': {
      const pending = e.deal?.priorityStatus === 'pending';
      const decide = (approve: boolean) => () => api(`/api/opportunities/${id}/priority/decide`, { method: 'POST', body: { approve, ...(comment.trim() ? { comment: comment.trim() } : {}) } });
      return (
        <Shell tag="High priority request" title={e.deal?.name ?? e.title} actions={pending ? <>{later}
          <button className="btn" disabled={a.busy || !comment.trim()} title={comment.trim() ? undefined : 'Write the reason for rejecting'} onClick={() => a.run(decide(false))}>Reject</button>
          <button className="btn primary" disabled={a.busy} onClick={() => a.run(decide(true))}>Approve</button></> : <>{openBtn}<button className="btn primary" onClick={onDone}>OK</button></>}>
          {body}{from}
          {pending
            ? <label className="field"><span className="label">Comment (required to reject)</span><textarea rows={2} maxLength={1000} value={comment} onChange={(x) => setComment(x.target.value)} /></label>
            : <p className="muted">This request has already been decided.</p>}
          {err}
        </Shell>
      );
    }
    case 'priority.approved':
    case 'priority.rejected':
      return (
        <Shell tag={e.kind === 'priority.approved' ? 'High priority approved' : 'High priority not approved'} title={e.deal?.name ?? e.title}
          actions={<>{openBtn}<button className="btn primary" onClick={onDone}>OK</button></>}>
          <p>{e.kind === 'priority.approved' ? 'Pre-sales has been told to handle it as soon as possible.' : 'The deal stays at normal priority.'}</p>
          {e.body && <blockquote className="popup-quote">{e.body}</blockquote>}{from}
        </Shell>
      );
    case 'deal.won':
      return (
        <Shell tag="Deal won" title={e.deal?.name ?? e.title} actions={<>{later}{openBtn}
          <button className="btn primary" disabled={a.busy} onClick={() => a.run(async () => {
            const r = await api<{ id: string }>(`/api/delivery/from-deal/${id}`, { method: 'POST', body: {} });
            navigate(`/projects/${r.id}`);
          })}>Start the project</button></>}>
          <p>The deal is won. Start its project to hand it over to execution: materials, testing checks and billing are prepared from the deal.</p>{from}{err}
        </Shell>
      );
    case 'vo.approval':
    case 'po.approval': {
      const decidePo = (approve: boolean) => () => api(`/api/delivery/${e.kind === 'vo.approval' ? 'vo' : 'po'}/${id}/decide`, { method: 'POST', body: { approve, ...(comment.trim() ? { comment: comment.trim() } : {}) } });
      return (
        <Shell tag={e.kind === 'vo.approval' ? 'Variation order approval' : 'Purchase request approval'} title={e.title} actions={<>{later}<button className="btn" onClick={() => open()}>Open</button>
          <button className="btn" disabled={a.busy || !comment.trim()} title={comment.trim() ? undefined : 'Write the reason for rejecting'} onClick={() => a.run(decidePo(false))}>Reject</button>
          <button className="btn primary" disabled={a.busy} onClick={() => a.run(decidePo(true))}>Approve</button></>}>
          {body}{from}
          <label className="field"><span className="label">Comment (required to reject)</span><textarea rows={2} maxLength={1000} value={comment} onChange={(x) => setComment(x.target.value)} /></label>
          {err}
        </Shell>
      );
    }
    case 'project.priority': {
      const decideP = (approve: boolean) => () => api(`/api/delivery/priority/${id}/decide`, { method: 'POST', body: { approve, ...(comment.trim() ? { comment: comment.trim() } : {}) } });
      return (
        <Shell tag="High priority request" title={e.title} actions={<>{later}<button className="btn" onClick={() => open()}>Open project</button>
          <button className="btn" disabled={a.busy || !comment.trim()} title={comment.trim() ? undefined : 'Write the reason for not approving'} onClick={() => a.run(decideP(false))}>Reject</button>
          <button className="btn primary" disabled={a.busy} onClick={() => a.run(decideP(true))}>Approve</button></>}>
          {body}{from}
          <label className="field"><span className="label">Comment (required to reject)</span><textarea rows={2} maxLength={1000} value={comment} onChange={(x) => setComment(x.target.value)} /></label>
          {err}
        </Shell>
      );
    }
    case 'project.priority_high':
      return (
        <Shell tag="HIGH PRIORITY" title={e.title} actions={<><button className="btn" onClick={() => open()}>Open project</button><button className="btn primary" onClick={onDone}>Got it</button></>}>
          <p><b>Please handle this as soon as possible.</b></p>{body}{from}
        </Shell>
      );
    case 'project.priority_decided':
    case 'vo.decided':
    case 'po.decided':
    case 'project.assigned':
    case 'project.ticket':
      return (
        <Shell tag={e.kind === 'project.ticket' ? 'Service ticket' : e.kind === 'po.decided' ? 'Purchase request' : e.kind === 'vo.decided' ? 'Variation order' : e.kind === 'project.priority_decided' ? 'High priority' : 'Project'} title={e.title} actions={<>{later}<button className="btn primary" onClick={() => open()}>Open project</button></>}>
          {body}{from}
        </Shell>
      );
    case 'accounts.invoice_request':
    case 'accounts.po_approved':
    case 'accounts.deal_won':
      return (
        <Shell tag={e.kind === 'accounts.invoice_request' ? 'Invoice request' : e.kind === 'accounts.po_approved' ? 'Approved purchase request' : 'Deal won'} title={e.title}
          actions={<>{later}<button className="btn primary" onClick={() => open()}>Open Accounts</button></>}>
          <p>Do it in Odoo, then record it on the Accounts tab.</p>{body}{from}
        </Shell>
      );
    case 'accounts.ready_weekly':
      return (
        <Shell tag="Ready to invoice" title={e.title} actions={<>{later}<button className="btn primary" onClick={() => open()}>Open Ready to invoice</button></>}>
          <p className="muted small">Waiting a week or more. Request each one, or mark it already in Odoo or not yet.</p>
          <ReadyRows body={e.body} />
        </Shell>
      );
    case 'accounts.invoice_done':
      return (
        <Shell tag="Accounts" title={e.title} actions={<>{later}<button className="btn primary" onClick={() => open()}>Open project</button></>}>
          {body}{from}
        </Shell>
      );
    case 'task.high':
      return (
        <Shell tag="High priority task" title={e.title} actions={<>{later}{openBtn}</>}>
          {e.body ? <blockquote className="popup-quote">{e.body}</blockquote> : null}
          <p>Handle it first: it is marked <b>HIGH</b> in your tasks.</p>{from}
        </Shell>
      );
    default:
      return (
        <Shell tag="New documents" title={e.title} actions={<>{later}{openBtn}</>}>
          {body}{from}
        </Shell>
      );
  }
}

const DealLink = ({ d, onGo }: { d: PopupDealRef; onGo: () => void }) => (
  <><Link to={dealUrl(d.id)} onClick={onGo}>{d.name}</Link>{d.accountName && <span className="muted"> · {d.accountName}</span>}</>
);

function CardPopup({ c, onDone }: { c: PopupCard; onDone: () => void }) {
  const { organization } = useSession();
  const go = onDone;
  switch (c.type) {
    case 'briefing_sales':
      return (
        <Shell tag="Good morning" title="Your day" actions={<button className="btn primary" autoFocus onClick={onDone}>Start my day</button>}>
          <ul className="popup-stats">
            {c.tasksToday + c.tasksOverdue > 0 && <li><b>{c.tasksToday}</b> task{c.tasksToday === 1 ? '' : 's'} due today{c.tasksOverdue ? <>, <b className="danger">{c.tasksOverdue}</b> overdue</> : null}</li>}
          </ul>
          <DealList title="Pre-sales needs documents" deals={c.clarifications} onGo={go} />
          <DealList title="Next action overdue" deals={c.nextActionOverdue} onGo={go} />
          <DealList title="Closing this week" deals={c.closingThisWeek} onGo={go} extra={(d) => formatDate(d.closeDate)} />
        </Shell>
      );
    case 'briefing_presales':
      return (
        <Shell tag="Good morning" title="Your pre-sales day" actions={<><Link className="btn" to="/presales" onClick={go}>Open My Desk</Link><button className="btn primary" autoFocus onClick={onDone}>Start my day</button></>}>
          <ul className="popup-stats">
            <li><b>{c.notStarted}</b> deal{c.notStarted === 1 ? '' : 's'} not started yet</li>
            {c.highPriority.length > 0 && <li><b className="danger">{c.highPriority.length}</b> High priority</li>}
            {c.deadlines.length > 0 && <li><b>{c.deadlines.length}</b> quotation deadline{c.deadlines.length === 1 ? '' : 's'} this week</li>}
          </ul>
          <DealList title="High priority" deals={c.highPriority} onGo={go} />
          <DealList title="Deadlines this week" deals={c.deadlines} onGo={go} extra={(d) => formatDate(d.deadline)} />
        </Shell>
      );
    case 'cold':
      return (
        <Shell tag="Deals going cold" title="No activity for more than 2 weeks" actions={<button className="btn primary" onClick={onDone}>Done</button>}>
          <p className="muted small">Set the next step so the deal moves again. It counts as activity once you log a call or meeting on the deal.</p>
          <ul className="popup-rows">{c.deals.map((d) => <ColdRow key={d.id} d={d} onGo={go} />)}</ul>
        </Shell>
      );
    case 'close_passed':
      return (
        <Shell tag="Close date passed" title="Update these deals" actions={<button className="btn primary" onClick={onDone}>Done</button>}>
          <p className="muted small">Their close date is in the past. Did you win, lose, or does it need a new date?</p>
          <ul className="popup-rows">{c.deals.map((d) => <ClosePassedRow key={d.id} d={d} onGo={go} />)}</ul>
        </Shell>
      );
    case 'deadline':
      return (
        <Shell tag="Quotation deadline" title={c.deals.length === 1 ? 'A quotation is due soon' : 'Quotations due soon'} actions={<button className="btn primary" onClick={onDone}>OK</button>}>
          <ul className="popup-rows">{c.deals.map((d) => <DeadlineRow key={d.id} d={d} onGo={go} />)}</ul>
        </Shell>
      );
    case 'projects_briefing':
      return (
        <Shell tag="Good morning" title="Your projects today" actions={<><Link className="btn" to="/projects/home" onClick={go}>Open my follow-ups</Link><button className="btn primary" autoFocus onClick={onDone}>Start my day</button></>}>
          <ul className="popup-stats">
            {c.priorities > 0 && <li><b className="danger">{c.priorities}</b> High priority item{c.priorities === 1 ? '' : 's'}</li>}
            <li><b className={c.overdue ? 'danger' : ''}>{c.overdue}</b> overdue · <b>{c.dueSoon}</b> due soon{c.tickets ? <> · <b>{c.tickets}</b> service ticket{c.tickets === 1 ? '' : 's'}</> : null}</li>
          </ul>
          {c.items.length > 0 && (
            <div className="popup-list"><h3>To follow up</h3><ul>{c.items.map((i, n) => (
              <li key={n}><Link to={`/projects/${i.projectId}`} onClick={go}>{i.title}</Link><span className={i.late ? 'danger' : 'muted'}> · {i.reason} · {i.projectNumber}</span></li>
            ))}</ul></div>
          )}
        </Shell>
      );
    case 'weekly':
      return (
        <Shell tag="Weekly snapshot" title={`Last week: ${formatDate(c.from)} – ${formatDate(c.to)}`}
          actions={<><Link className="btn" to="/kpi" onClick={go}>Open KPIs</Link><button className="btn primary" autoFocus onClick={onDone}>Got it</button></>}>
          <div className="table-wrap flat">
            <table>
              <thead><tr><th>Project</th><th className="num">Registered</th><th className="num">Quoted</th><th className="num">Won</th><th className="num">Lost</th><th className="num">Won value</th></tr></thead>
              <tbody>{c.rows.map((r) => (
                <tr key={r.source} className="static"><td>{SOURCE[r.source] ?? r.source}</td><td className="num">{r.registered}</td><td className="num">{r.quoted}</td><td className="num">{r.won}</td><td className="num">{r.lost}</td><td className="num">{formatMoney(r.wonValue, organization.currency)}</td></tr>
              ))}</tbody>
            </table>
          </div>
        </Shell>
      );
  }
}

function DealList<T extends PopupDealRef>({ title, deals, onGo, extra }: { title: string; deals: T[]; onGo: () => void; extra?: (d: T) => string }) {
  if (!deals.length) return null;
  return (
    <div className="popup-list">
      <h3>{title}</h3>
      <ul>{deals.map((d) => <li key={d.id}><DealLink d={d} onGo={onGo} />{extra && <span className="muted"> · {extra(d)}</span>}</li>)}</ul>
    </div>
  );
}

/** A row that disappears once its action is done. */
function useRow() {
  const qc = useQueryClient();
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: ({ fn }: { fn: () => Promise<unknown>; label: string }) => fn(),
    onSuccess: (_r, v) => { setDone(v.label); void qc.invalidateQueries({ queryKey: ['record'] }); void qc.invalidateQueries({ queryKey: ['records'] }); },
    onError: (e) => setError(errMsg(e)),
  });
  return { run: (label: string, fn: () => Promise<unknown>) => { setError(null); m.mutate({ fn, label }); }, busy: m.isPending, done, error };
}

const patchDeal = (id: string, body: Record<string, unknown>) => () => api(`/api/records/opportunity/${id}`, { method: 'PATCH', body });
const inDays = (n: number) => riyadhDay(n);

function ColdRow({ d, onGo }: { d: PopupDealRef & { daysIdle: number | null }; onGo: () => void }) {
  const r = useRow();
  const [step, setStep] = useState('');
  const [date, setDate] = useState(inDays(2));
  return (
    <li>
      <div><DealLink d={d} onGo={onGo} />{d.daysIdle != null && <span className="muted"> · {d.daysIdle} days without activity</span>}</div>
      {r.done ? <p className="notice small">✓ {r.done}</p> : (
        <form className="popup-inline" onSubmit={(e) => { e.preventDefault(); if (step.trim()) r.run('Next step saved', patchDeal(d.id, { nextStep: step.trim(), nextActionDate: date })); }}>
          <input aria-label={`Next step for ${d.name}`} placeholder="Next step, e.g. Call the consultant" maxLength={255} value={step} onChange={(e) => setStep(e.target.value)} />
          <input aria-label="Next action date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          <button className="btn small primary" disabled={r.busy || !step.trim()}>Save</button>
        </form>
      )}
      {r.error && <p className="form-error small">{r.error}</p>}
    </li>
  );
}

function ClosePassedRow({ d, onGo }: { d: PopupDealRef & { closeDate: string; wonStageId: string | null; lostStageId: string | null }; onGo: () => void }) {
  const r = useRow();
  const [date, setDate] = useState(inDays(14));
  const [moving, setMoving] = useState(false);
  return (
    <li>
      <div><DealLink d={d} onGo={onGo} /><span className="muted"> · was due {formatDate(d.closeDate)}</span></div>
      {r.done ? <p className="notice small">✓ {r.done}</p> : moving ? (
        <form className="popup-inline" onSubmit={(e) => { e.preventDefault(); r.run(`Moved to ${formatDate(date)}`, patchDeal(d.id, { closeDate: date })); }}>
          <input aria-label="New close date" type="date" value={date} min={inDays(0)} onChange={(e) => setDate(e.target.value)} />
          <button className="btn small primary" disabled={r.busy}>Save</button>
          <button type="button" className="btn small ghost" onClick={() => setMoving(false)}>Cancel</button>
        </form>
      ) : (
        <div className="popup-inline">
          {d.wonStageId && <button className="btn small" disabled={r.busy} onClick={() => r.run('Marked as won', patchDeal(d.id, { stageId: d.wonStageId }))}>Won</button>}
          {d.lostStageId && <button className="btn small" disabled={r.busy} onClick={async () => { if ((await confirmDialog(`Mark “${d.name}” as lost?`))) r.run('Marked as lost', patchDeal(d.id, { stageId: d.lostStageId })); }}>Lost</button>}
          <button className="btn small primary" disabled={r.busy} onClick={() => setMoving(true)}>New date…</button>
        </div>
      )}
      {r.error && <p className="form-error small">{r.error}</p>}
    </li>
  );
}

function DeadlineRow({ d, onGo }: { d: PopupDealRef & { deadline: string; daysLeft: number; presalesStatus: string | null }; onGo: () => void }) {
  const when = d.daysLeft <= 0 ? 'today' : d.daysLeft === 1 ? 'tomorrow' : `in ${d.daysLeft} days`;
  return (
    <li>
      <div><DealLink d={d} onGo={onGo} /><span className={d.daysLeft <= 1 ? 'danger' : 'muted'}> · due {when} ({formatDate(d.deadline)})</span></div>
      {d.presalesStatus !== 'quotation_ready' && (
        // Quotation ready is marked in My Desk (it also fills Our Quoted Brands from the costing workbook).
        <div className="popup-inline"><Link className="btn small primary" to="/presales" onClick={onGo}>Open in My Desk</Link></div>
      )}
    </li>
  );
}

/**
 * The Sunday Ready to invoice reminder: its lines ("• PRJ-0009 · Telephone Set - Hilton · Advance payment · SAR 35,500")
 * as rows: project number, name with what to invoice under it, the amount on the right. Other lines ("…and 3 more") as text.
 */
function ReadyRows({ body }: { body: string | null }) {
  if (!body) return null;
  return (
    <ul className="ready-rows">
      {body.split('\n').map((l, i) => {
        const parts = l.replace(/^•\s*/, '').split(' · ');
        if (parts.length < 2) return <li key={i} className="ready-more muted small">{l}</li>;
        const amount = /^SAR [\d,]+$/.test(parts[parts.length - 1]!) ? parts.pop()! : null;
        const number = /^[A-Z]+-\d+$/.test(parts[0]!) ? parts.shift()! : null;
        const [name, ...what] = parts;
        return (
          <li key={i}>
            <span className="ready-main">
              <span className="ready-name">{number && <b className="ready-num">{number}</b>}{name}</span>
              {what.length > 0 && <span className="muted small">{what.join(' · ')}</span>}
            </span>
            <span className="ready-amt">{amount ?? '—'}</span>
          </li>
        );
      })}
    </ul>
  );
}
